import { takeUntil } from 'rxjs/operators';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Product } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { Subject } from 'rxjs';

import { SdPushSliderComponent } from '@seb/web-design-system/push-slider';
import {
  BrandsEnum, BrandThemesEnum, IComponentWrapper, IExtendedLink,
  IHeading, ILinkMedia,
  ImageRatioEnum, IPushCard,
  IPushCardWithLayout,
  PositionEnum, SdComponentWrapperComponent,
  SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { LameConfig } from '@seb/web-common/core/models';
import { BrandCornerMapper, HeadingParagraphMapper, toHeading } from '@seb/web-common/core/mappers';
import { ConfigurationService, ImageService, TrackingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-product-how-to',
  templateUrl: './product-how-to.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdComponentWrapperComponent, SdPushSliderComponent]
})
export class ProductHowToComponent implements OnInit, OnDestroy {
  heading: IHeading;
  mediaConfig = PositionEnum.top;
  themesConfig = ThemesEnum.light;
  imageRatio = ImageRatioEnum.r16_9;
  sizesEnum = SizesEnum.xs;
  pushs: IPushCardWithLayout[] = [];
  wrapperContent: IComponentWrapper;
  lameConfig: LameConfig;
  private readonly destroy$: Subject<void> = new Subject();

  @Input() brand = BrandsEnum.default;

  constructor(
    private readonly currentProductService: CurrentProductService,
    private readonly imageService: ImageService,
    private trackingService: TrackingService,
    private configService: ConfigurationService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private brandCornerMapper: BrandCornerMapper
  ) {}

  ngOnInit(): void {
    const config = this.configService.config;
    const product$ = this.currentProductService.getProduct();
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.howTo || {};

    product$
      .pipe(takeUntil(this.destroy$))
      .subscribe((product) => {
        this.sizesEnum = (config?.components?.pushSlider?.sizeForce as SizesEnum) || this.sizesEnum;
        if (product?.howTo) {
          this.heading = toHeading({ headline: product.howTo.mainTitle, subline: product.howTo.mainDescription });
          const richParagraphData = {
            heading: {
              headline: product.howTo.mainTitle,
              subline: product.howTo.mainDescription
            },
            titleTheme: this.lameConfig?.titleTheme || BrandThemesEnum.brand,
            isUnderlined: !!this.lameConfig?.titleUnderlined,
            size: SizesEnum.l
          };
          this.wrapperContent = {
            titleLevel: 2,
            links: [],
            footNotes: [],
            heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
          };
          const howToPushData: IPushCard[] = this.getHowToPushData(product);
          this.pushs = this.mapToPushCardWithLayout(howToPushData);
          this.cdRef.markForCheck();
        }
      });
  }

  mapToPushCardWithLayout(pushCards: IPushCard[]): IPushCardWithLayout[] {
    return pushCards.map((pushCard: IPushCard) => ({
      push: pushCard,
      themesConfig: this.themesConfig,
      textConfig: PositionEnum.center,
      mediaConfig: this.mediaConfig,
      imageRatio: this.imageRatio,
      mediaAsBackground: false,
      isDescription: true
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getHowToPushData(product: Product): IPushCard[] {
    const pushData: IPushCard[] = [];
    const pushConfig = {
      ...((this.lameConfig?.pushTheme && { themesConfig: this.lameConfig?.pushTheme }) || {}),
      ...((this.lameConfig?.titleTheme && { titleThemesConfig: this.lameConfig?.titleTheme }) || {})
    };
    let richParagraphData = {};

    for (let i = 1; i <= 4; i++) {
      const titleKey = `title${i}`;
      const detailKey = `detail${i}`;
      const mediaKey = `media${i}`;

      if (product.howTo[titleKey] && product.howTo[detailKey]) {
        richParagraphData = {
          heading: {
            headline: product.howTo[titleKey],
            description: product.howTo[detailKey]
          },
          themesConfig: ((this.lameConfig?.pushTheme && { themesConfig: this.lameConfig?.pushTheme }) || {}),
          titleTheme: ((this.lameConfig?.titleTheme && { titleThemesConfig: this.lameConfig?.titleTheme }) || {})
        };

        pushData.push({
          richParagraph: {
            ...pushConfig,
            heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
          },
          media: this.imageService.normalizeMedia(product.howTo[mediaKey]?.find((image) => image.format === 'main'))
        });
      }
    }

    return pushData;
  }

  onClickedHowTo(event: IExtendedLink | ILinkMedia): void {
    this.trackingService.sendEvent('clickProductAssistance', {
      assistanceName: event.linkName
    });
  }
}
